/**
 * V12 数据中心 — 战斗历史模块
 *
 * 每场战斗结束后保存一条 BattleRecord，
 * 在屏 2（编辑器）“战斗历史”Tab 中查看。
 *
 * @see design/v12-user-data-center/spec.md §FR-005
 */

import { dataProxy } from "./data-proxy.js";

// ============================================================
// 数据类型
// ============================================================

export interface BattleRecord {
  /** 战斗唯一 ID */
  id: string;
  /** 关联的项目 ID */
  projectId: string;
  /** 玩家所属队伍 */
  playerTeam: "A" | "B";
  /** 战斗结果 */
  result: "win" | "loss" | "draw";
  /** 对手名称（AI 名称或 "AI Easy Aggressive"） */
  opponentName: string;
  /** 对手代码（AI Python 代码） */
  opponentCode: string;
  /** 战斗持续 tick 数 */
  battleDuration: number;
  /** 战斗时间戳 */
  timestamp: number;
  /** 关卡 ID */
  levelId: string;
  /** 地图名称 */
  mapName: string;
  /** 自动生成的描述 */
  description: string;
  /** 玩家剩余 HP / 初始 HP 比例 */
  hpRatio: number;
  /** 战斗结束时玩家剩余 HP */
  remainingHP: number;
  /** 战斗结束时敌方剩余 HP */
  enemyRemainingHP: number;
}

// ============================================================
// 辅助类型
// ============================================================

export interface CreateBattleRecordInput {
  projectId: string;
  playerTeam: "A" | "B";
  result: "win" | "loss" | "draw";
  opponentName: string;
  opponentCode: string;
  battleDuration: number;
  levelId: string;
  mapName: string;
  remainingHP: number;
  enemyRemainingHP: number;
}

// ============================================================
// 存储操作
// ============================================================

function generateBattleId(): string {
  return `btl_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function generateDescription(result: "win" | "loss" | "draw", hpRatio: number): string {
  switch (result) {
    case "win":
      return hpRatio > 0.7
        ? "碾压式胜利"
        : hpRatio > 0.3
        ? "险胜"
        : "残血翻盘";
    case "loss":
      return "被击败";
    case "draw":
      return "打成平手";
  }
}

// ============================================================
// 公开 API
// ============================================================

/** 获取所有战斗记录（按时间倒序） */
export function getBattleRecords(): BattleRecord[] {
  const records = dataProxy.get<BattleRecord[]>("battle-history") ?? [];
  return records.sort((a, b) => b.timestamp - a.timestamp);
}

/** 获取指定项目的战斗记录 */
export function getBattleRecordsByProject(projectId: string): BattleRecord[] {
  return getBattleRecords().filter((r) => r.projectId === projectId);
}

/** 创建一条战斗记录 */
export function createBattleRecord(input: CreateBattleRecordInput): BattleRecord {
  const hpRatio =
    input.remainingHP + input.enemyRemainingHP > 0
      ? input.remainingHP / (input.remainingHP + input.enemyRemainingHP)
      : input.result === "win" ? 1 : 0;

  const record: BattleRecord = {
    id: generateBattleId(),
    projectId: input.projectId,
    playerTeam: input.playerTeam,
    result: input.result,
    opponentName: input.opponentName,
    opponentCode: input.opponentCode,
    battleDuration: input.battleDuration,
    timestamp: Date.now(),
    levelId: input.levelId,
    mapName: input.mapName || "Unknown",
    description: generateDescription(input.result, hpRatio),
    hpRatio,
    remainingHP: input.remainingHP,
    enemyRemainingHP: input.enemyRemainingHP,
  };

  const records = dataProxy.get<BattleRecord[]>("battle-history") ?? [];
  records.push(record);

  // 最多保留 100 条
  if (records.length > 100) {
    records.splice(0, records.length - 100);
  }

  dataProxy.set("battle-history", records);
  return record;
}

/** 删除指定战斗记录 */
export function deleteBattleRecord(id: string): void {
  const records = getBattleRecords().filter((r) => r.id !== id);
  dataProxy.set("battle-history", records);
}

/** 清空所有战斗记录 */
export function clearBattleRecords(): void {
  dataProxy.remove("battle-history");
}

/** 获取战斗记录总数 */
export function getBattleRecordCount(): number {
  return getBattleRecords().length;
}

/** 获取指定项目的战斗记录数 */
export function getBattleRecordCountByProject(projectId: string): number {
  return getBattleRecordsByProject(projectId).length;
}
